/**
 * ENVANTER RAPORU — ortak tanımlar (etiketler, sütunlar, süzgeç künyesi) + XLSX çalışma kitabı.
 *
 * Ekran önizleme / A4 (pages/StockReportPrint.tsx) ve Excel AYNI uçtan (`inventory.stockReport`), AYNI süzgeçle
 * ve AYNI veriyle beslenir: Excel, ekranda o an görünen yanıtın kendisinden kurulur (ikinci istek yok). Sütun
 * listesi, sınıf etiketleri ve süzgeç künyesi BU dosyada tek yerde tanımlıdır — ekran ile dosya ayrışmasın.
 *
 * NEDEN exceljs (SheetJS DEĞİL): projede `xlsx@0.18` de var ama SheetJS'in community sürümü HÜCRE BİÇİMİ YAZMAZ
 * (kalın başlık, dolgu, çerçeve, donuk bölme yok). `exceljs` TEMBEL yüklenir (dynamic import) → yalnız rapor
 * alınırken indirilir, ana paket şişmez. Bu modülün geri kalanı (etiketler) hafiftir; statik içe aktarılabilir.
 *
 * YAPI:
 *  · "Genel Durum": künye + uygulanan süzgeçler + KPI + BİRİM BAZINDA toplam (karışık birim toplanmaz)
 *    + kart türüne göre özet + uyarılar (değeri bilinmeyen, kapsam dışı, "Yalnız stokta" ile düşen kayıt açıkları).
 *  · Her kart türü için AYRI sayfa: ürün görünümünde ürün satırları, lot görünümünde lot satırları.
 *
 * OKUNAKLIK: başlık satırı donuk + otomatik süzgeç; sayılar TR biçimli ve GERÇEK sayı (Excel'de toplanabilsin —
 * metin yazmak formülleri kırar); değeri bilinmeyen hücre BOŞ bırakılır (0 yazılmaz: 0 "bedava" demektir).
 * Sınıf sütunu renkli dolgu + METİN (renk tek başına anlam taşımaz).
 */
import type { inferRouterOutputs } from "@trpc/server";
import type { AppRouter } from "@steperp/api/src/routers/index.js";
import { istanbulGunu } from "./sktKurali";

/** Sunucudaki `inventory.stockReport` yanıtı — tip uçtan türetilir (elle kopya yok → alan adı kayarsa derleme kırılır). */
export type StockReportData = inferRouterOutputs<AppRouter>["inventory"]["stockReport"];
export type StockReportRow = StockReportData["rows"][number];
export type StockReportLot = StockReportData["lotlar"][number];
export type ReportKind = StockReportRow["kind"];
export type LotSinifi = StockReportLot["sinif"];
export type LotMaliyetNotu = NonNullable<StockReportLot["lotMaliyetiNotu"]>;

export const KIND_ETIKET: Record<ReportKind, string> = { stock: "Ham Madde", semi: "Yarı Mamul", finished: "Mamul" };
export const KIND_SIRASI: readonly ReportKind[] = ["stock", "semi", "finished"];
export const SINIF_SIRASI: readonly LotSinifi[] = ["saglam", "skt", "blokeli", "acik"];
export const SINIF_ETIKET: Record<LotSinifi, string> = { saglam: "Sağlam", skt: "SKT Geçmiş", blokeli: "Blokeli", acik: "Kayıt Açığı" };
/** Sınıf tanımı — lejant (ekran) ve Genel Durum sayfası (Excel) AYNI metni basar. Tanımın kendisi: api/src/mrp.ts `lotSinifi`. */
export const SINIF_TANIM: Record<LotSinifi, string> = {
  saglam: "Serbest (kullanılabilir), SKT'si geçmemiş.",
  skt: "SKT tarihi geçmiş ya da lot 'SKT geçti' damgalı — kullanılamaz.",
  blokeli: "Kalite kontrol bekliyor, karantinada ya da reddedilmiş — kullanılamaz.",
  acik: "Eksi bakiye (defter açığı) — stok değildir, değerlenmez.",
};
/** Ham lot durumu → okunur ek bilgi (sınıf rozetinin yanında). */
export const LOT_DURUM_ETIKET: Record<StockReportLot["status"], string> = {
  released: "Serbest", pending_qc: "QC bekliyor", on_hold: "Karantina", expired: "SKT geçti", rejected: "Red", consumed: "Tükendi damgalı",
};
export const KAYNAK_ETIKET: Record<"lot" | "liste", string> = { lot: "Lot maliyeti", liste: "Alış fiyat listesi" };
/** Lotun KENDİ maliyeti neden kullanılmadı — tanım: api/src/stock-value.ts `lotMaliyetKarari`. */
export const MALIYET_NOTU_ETIKET: Record<LotMaliyetNotu, string> = {
  doviz: "lot maliyeti dövizli fişten",
  kismi: "lot maliyeti kısmi (iş emri çıktısı)",
  belirsiz: "lot maliyetinin para birimi belirsiz",
};
/** "Maliyet Kaynağı" metni — ekran (alt not) ve Excel sütunu AYNI metni basar. */
export function maliyetKaynagiMetni(l: Pick<StockReportLot, "maliyetKaynagi" | "lotMaliyetiNotu" | "qty">): string {
  const not = l.lotMaliyetiNotu ? ` (${MALIYET_NOTU_ETIKET[l.lotMaliyetiNotu]})` : "";
  if (l.maliyetKaynagi) return `${KAYNAK_ETIKET[l.maliyetKaynagi]}${not}`;
  return l.qty > 0 ? `Bilinmiyor${not}` : "—";
}
export const TUR_ATANMAMIS = "Tür atanmamış";

/**
 * TAKVİM GÜNÜ — ekran ve Excel AYNI fonksiyondan (Europe/Istanbul; sunucunun SKT kuralı `istanbulGunu` ile aynı gün
 * tanımı). Excel'e ham `new Date(iso)` yazılırsa exceljs anı UTC'ye göre seri sayıya çevirir: 00:00–02:59
 * (İstanbul) arasında üretilen lot ekranda 14.10, Excel'de 13.10 görünürdü (gece vardiyasında her gün).
 */
export function raporGunu(iso: string | null | undefined): { y: number; m: number; d: number } | null {
  if (!iso) return null;
  const g = /^(\d{4,})-(\d{2})-(\d{2})$/.exec(istanbulGunu(iso));
  return g ? { y: Number(g[1]), m: Number(g[2]), d: Number(g[3]) } : null;
}
const p2 = (n: number) => String(n).padStart(2, "0");
/** Ekrandaki tarih metni "14.10.2026" (yoksa "—"). */
export function raporGunMetni(iso: string | null | undefined): string {
  const g = raporGunu(iso);
  return g ? `${p2(g.d)}.${p2(g.m)}.${String(g.y).padStart(4, "0")}` : "—";
}
/** Excel hücresi: aynı takvim gününün UTC gece yarısı → seri sayı TAM gün, hücre ekrandaki günü gösterir. */
function excelGunu(iso: string | null | undefined): Date | null {
  const g = raporGunu(iso);
  return g ? new Date(Date.UTC(g.y, g.m - 1, g.d)) : null;
}
const zamanFmt = new Intl.DateTimeFormat("tr-TR", { timeZone: "Europe/Istanbul", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
/** Künye tarih-saati (Europe/Istanbul) — ekran ve Excel aynı. */
export function raporZamanMetni(iso: string): string { return zamanFmt.format(new Date(iso)); }

const sayiTr = (n: number) => n.toLocaleString("tr-TR");
export type RaporUyarisi = { ton: "dgr" | "wrn"; metin: string };
/**
 * Belge uyarıları — sessizlik yasağı: ekran ve Excel AYNI listeyi basar (biri eksik kalmasın).
 * Ekrana özgü olan yalnız "süzgeç güncellenemedi" hatasıdır (istek durumu, belge içeriği değil).
 */
export function raporUyarilari(d: StockReportData): RaporUyarisi[] {
  const lotGorunumu = d.suzgec.gorunum === "lot";
  const out: RaporUyarisi[] = [];
  if (d.tesisYok) out.push({ ton: "dgr", metin: "Hesabınıza tesis atanmamış; rapor bu yüzden boş. Yöneticinizden tesis ataması isteyin." });
  if (d.ozet.degerBilinmeyenLot > 0) {
    out.push({ ton: "wrn", metin: `${sayiTr(d.ozet.degerBilinmeyenLot)} lotun maliyeti/₺ fiyatı yok; değerleri toplama 0 diye KATILMADI — stok değeri bir alt sınırdır. ${lotGorunumu ? "Bu lotların değeri “bilinmiyor” olarak gösterilir (0 yazılmaz); ürün toplamında miktarları ayrıca belirtilir." : "Miktarları “Değeri Bilinmeyen” sütunundadır."}` });
  }
  if (d.ozet.lotMaliyetiKullanilmayan > 0) {
    out.push({ ton: "wrn", metin: `${sayiTr(d.ozet.lotMaliyetiKullanilmayan)} lotun kendi maliyeti KULLANILMADI: para birimi ₺ olduğu kanıtlanamadı (dövizli fiş, PO kabulü, tedarikçisiz fiş) ya da maliyet kısmi (iş emri çıktısı). Ham maddede alış fiyat listesine düşüldü; liste fiyatı da yoksa değeri bilinmiyor sayıldı.` });
  }
  if (d.kapsamDisi.lotSayisi > 0) {
    out.push({ ton: "wrn", metin: `Süzgece uyan ${sayiTr(d.kapsamDisi.lotSayisi)} lot (${sayiTr(d.kapsamDisi.kartSayisi)} kart) rapora giremedi: kartı pasif ya da bu tesise ait değil. ${d.kapsamDisi.kartlar.join("; ")}${d.kapsamDisi.kartSayisi > d.kapsamDisi.kartlar.length ? " …" : ""}` });
  }
  if (d.yalnizAcik.kartSayisi > 0) {
    // "Yalnız stokta" tanımı sunucuda (`envanterRaporu`): artı bakiyeli lotu olan kart. Düşen kart SESSİZCE kaybolmaz.
    const acik = d.yalnizAcik.birimler.map((b) => `${sayiTr(b.acik)} ${b.uom}`).join(" · ");
    out.push({ ton: "wrn", metin: `${sayiTr(d.yalnizAcik.kartSayisi)} kartta yalnız kayıt açığı var (${sayiTr(d.yalnizAcik.lotSayisi)} lot${acik ? `, toplam ${acik}` : ""}) — stok olmadığı için “Yalnız stokta” açıkken listelenmedi; görmek için “Yalnız stokta”yı kapatın. ${d.yalnizAcik.kartlar.join("; ")}${d.yalnizAcik.kartSayisi > d.yalnizAcik.kartlar.length ? " …" : ""}` });
  }
  if (lotGorunumu && !d.suzgec.sadeceStokta) {
    const lotsuz = d.rows.filter((r) => r.lotSatiri === 0).length;
    if (lotsuz > 0) {
      // Sınıf süzgeci açıksa bu ürünlerin stoğu OLABİLİR — yalnız seçili sınıflarda lotları yoktur.
      const tumSiniflar = d.suzgec.siniflar.length === SINIF_SIRASI.length;
      out.push({ ton: "wrn", metin: `${tumSiniflar ? "Stoğu sıfır" : "Seçili sınıflarda lotu olmayan"} ${sayiTr(lotsuz)} ürünün lot tablosunda satırı yok (toplamlarda sayılır; ürün bazında görünümde listelenir).` });
    }
  }
  return out;
}

/** Uygulanan süzgeç — künye satırları (ekran + Excel). Sunucunun YANKILADIĞI süzgeçten yazılır (istemci state'i değil). */
export function suzgecOzeti(s: StockReportData["suzgec"]): Array<{ etiket: string; deger: string }> {
  const turler = s.kartTurleri.map((t) => t.name);
  if (s.turAtanmamis) turler.push(TUR_ATANMAMIS);
  return [
    { etiket: "Görünüm", deger: s.gorunum === "lot" ? "Lot bazında (ürünler lotlara ayrılmış)" : "Ürün bazında (lotlar birleşik)" },
    { etiket: "Tür", deger: s.kinds.length === 0 ? "Seçilmedi" : s.kinds.length === KIND_SIRASI.length ? "Tümü" : s.kinds.map((k) => KIND_ETIKET[k]).join(", ") },
    { etiket: "Kart türü", deger: turler.length ? turler.join(", ") : "Tümü" },
    { etiket: "Sınıf", deger: s.siniflar.length === 0 ? "Seçilmedi" : s.siniflar.length === SINIF_SIRASI.length ? "Tümü" : s.siniflar.map((x) => SINIF_ETIKET[x]).join(", ") },
    { etiket: "Stok", deger: s.sadeceStokta ? "Yalnız stokta olanlar (artı bakiyeli lotu olan kartlar)" : "Stoğu sıfır ve yalnız kayıt açığı olan kartlar dahil" },
  ];
}

/** Görünür sınıf sütunları — seçilmeyen sınıfın sütunu (hep 0) ne ekranda ne dosyada basılır. */
export function gorunurSiniflar(d: StockReportData): LotSinifi[] {
  return SINIF_SIRASI.filter((s) => d.suzgec.siniflar.includes(s));
}

/** Kart türü grubu anahtarı — null grup "Tür atanmamış" adıyla EN SONA. */
export function grupAdi(g: string | null): string { return g ?? TUR_ATANMAMIS; }
export function gruplariSirala(adlar: Iterable<string>): string[] {
  return [...new Set(adlar)].sort((a, b) => (a === TUR_ATANMAMIS ? 1 : b === TUR_ATANMAMIS ? -1 : a.localeCompare(b, "tr")));
}

/** Dosya adı: görünüm + zaman damgası — aynı gün birden çok rapor birbirini ezmesin. */
export function stockReportFileName(at: Date, gorunum: "urun" | "lot" = "urun"): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `envanter-raporu_${gorunum === "lot" ? "lot-bazinda" : "urun-bazinda"}_${at.getFullYear()}-${p(at.getMonth() + 1)}-${p(at.getDate())}_${p(at.getHours())}${p(at.getMinutes())}${p(at.getSeconds())}.xlsx`;
}

// ── Excel biçimleri ─────────────────────────────────────────────────────
/** Sınıf dolgu/yazı renkleri — CLAUDE.md paletinin ARGB karşılıkları (Excel CSS token okumaz). */
const SINIF_FILL: Record<LotSinifi, string> = { saglam: "FFDCFCE7", skt: "FFFEE2E2", blokeli: "FFFEF3C7", acik: "FFFEE2E2" };
const SINIF_FONT: Record<LotSinifi, string> = { saglam: "FF15803D", skt: "FFB91C1C", blokeli: "FFB45309", acik: "FFB91C1C" };
const INK = "FF14253E";        // primary (GENTAŞ lacivert)
const HEAD_FILL = "FF14253E";
const SUBTLE = "FFEAEEF5";     // primary-subtle
const MUTED = "FF71717A";      // neutral-500
const ZEBRA = "FFFAFAFA";      // neutral-50
const QTY_FMT = "#,##0.####";
const MONEY_FMT = "#,##0.00 ₺";
const UNIT_MONEY_FMT = "#,##0.00#### ₺";
const DATE_FMT = "dd.mm.yyyy";

type Kolon = { header: string; width: number; num?: string; hucre: (x: never) => unknown; sinif?: LotSinifi };

/** Ürün görünümü sütunları — ekranla aynı içerik (ekranda Tür/Kart Türü/Kod/Ad tek hücrede toplanır). */
function urunKolonlari(d: StockReportData): Kolon[] {
  const k = (c: Omit<Kolon, "hucre"> & { hucre: (r: StockReportRow) => unknown }) => c as unknown as Kolon;
  return [
    k({ header: "Tür", width: 12, hucre: (r) => KIND_ETIKET[r.kind] }),
    k({ header: "Kart Türü", width: 18, hucre: (r) => grupAdi(r.groupName) }),
    k({ header: "Kod", width: 18, hucre: (r) => r.code }),
    k({ header: "Ürün Adı", width: 40, hucre: (r) => r.name }),
    k({ header: "Birim", width: 8, hucre: (r) => r.uom }),
    ...gorunurSiniflar(d).map((s) => k({ header: SINIF_ETIKET[s], width: 13, num: QTY_FMT, sinif: s, hucre: (r) => r[s] })),
    k({ header: "Net", width: 13, num: QTY_FMT, hucre: (r) => r.onHand }),
    k({ header: "Lot", width: 7, num: "#,##0", hucre: (r) => r.lotCount }),
    k({ header: "En Yakın SKT", width: 13, num: DATE_FMT, hucre: (r) => excelGunu(r.nearestExpiry) }),
    // Hiç değeri bilinen lotu yoksa hücre BOŞ (0 yazılmaz); kısmi bilinende bilinen kısım + "Değeri Bilinmeyen" sütunu.
    k({ header: "Stok Değeri (₺)", width: 16, num: MONEY_FMT, hucre: (r) => (r.stockValue === 0 && r.degerBilinmeyen > 0 ? null : r.stockValue) }),
    k({ header: "Değeri Bilinmeyen", width: 16, num: QTY_FMT, hucre: (r) => (r.degerBilinmeyen > 0 ? r.degerBilinmeyen : null) }),
  ];
}

/** Lot görünümü sütunları — ekranla aynı içerik (ekranda ürün bilgisi grup başlığında). */
function lotKolonlari(): Kolon[] {
  const k = (c: Omit<Kolon, "hucre"> & { hucre: (r: StockReportLot) => unknown }) => c as unknown as Kolon;
  return [
    k({ header: "Tür", width: 12, hucre: (r) => KIND_ETIKET[r.itemKind] }),
    k({ header: "Kart Türü", width: 18, hucre: (r) => grupAdi(r.groupName) }),
    k({ header: "Ürün Kodu", width: 18, hucre: (r) => r.itemCode }),
    k({ header: "Ürün Adı", width: 36, hucre: (r) => r.itemName }),
    k({ header: "Lot", width: 18, hucre: (r) => r.code }),
    k({ header: "Üretim", width: 12, num: DATE_FMT, hucre: (r) => excelGunu(r.producedAt) }),
    k({ header: "SKT", width: 12, num: DATE_FMT, hucre: (r) => excelGunu(r.expiresAt) }),
    k({ header: "Sınıf", width: 13, hucre: (r) => SINIF_ETIKET[r.sinif] }),
    k({ header: "Durum", width: 14, hucre: (r) => LOT_DURUM_ETIKET[r.status] }),
    k({ header: "Lokasyon", width: 28, hucre: (r) => r.lokasyonlar.map((l) => (r.lokasyonlar.length > 1 ? `${l.ad} (${l.qty.toLocaleString("tr-TR")})` : l.ad)).join(", ") }),
    k({ header: "Miktar", width: 13, num: QTY_FMT, hucre: (r) => r.qty }),
    k({ header: "Birim", width: 8, hucre: (r) => r.uom }),
    k({ header: "Birim Maliyet", width: 14, num: UNIT_MONEY_FMT, hucre: (r) => r.birimMaliyet }),
    k({ header: "Maliyet Kaynağı", width: 26, hucre: (r) => maliyetKaynagiMetni(r) }),
    k({ header: "Değer (₺)", width: 15, num: MONEY_FMT, hucre: (r) => r.deger }),
    k({ header: "İlişkili Sipariş", width: 34, hucre: (r) => r.siparisler.map((s) => `${s.iliski === "sevk" ? "Sevk: " : ""}${s.code}${s.musteri ? ` · ${s.musteri}` : ""}`).join("; ") }),
  ];
}

/**
 * Excel sayfa adı kuralı: en çok 31 karakter, `[]:*?/\` yasak, boş olamaz ve çalışma kitabında BENZERSİZ olmalı.
 * Kart türü adları kullanıcı verisidir — sanitize edilmezse kitap kurulamaz ya da ikinci sayfa ilkini ezer.
 */
function safeSheetName(raw: string, used: Set<string>): string {
  let s = (raw || TUR_ATANMAMIS).replace(/[[\]:*?/\\]/g, "-").trim().slice(0, 31) || TUR_ATANMAMIS;
  if (used.has(s.toLowerCase())) {
    let i = 2;
    while (used.has(`${s.slice(0, 28)} (${i})`.toLowerCase())) i++;
    s = `${s.slice(0, 28)} (${i})`;
  }
  used.add(s.toLowerCase());
  return s;
}

/**
 * Çalışma kitabını KURAR (indirmez). İndirme DOM'a bağlı (`document`, `Blob`) ve Node'dan test edilemez;
 * kitabın kendisi saf veri dönüşümüdür — gerçek dosya üretilip GERİ OKUNARAK doğrulanabilir.
 */
export async function buildStockReportWorkbook(data: StockReportData) {
  /** exceljs CJS yayınlıyor: tarayıcıda Vite interop sarar, Node'da yalnız `default` altında gelir — ikisini de karşıla. */
  const mod = (await import("exceljs")) as unknown as { default?: typeof import("exceljs") } & typeof import("exceljs");
  const ExcelJS = mod.default ?? mod;
  type WS = ReturnType<InstanceType<typeof ExcelJS.Workbook>["addWorksheet"]>;
  const wb = new ExcelJS.Workbook();
  wb.creator = "STEPERP";
  wb.created = new Date(data.generatedAt);
  const lotGorunumu = data.suzgec.gorunum === "lot";
  const siniflar = gorunurSiniflar(data);

  const baslikSatiri = (ws: WS, satir: number, basliklar: Array<{ header: string; num?: string }>) => {
    const hr = ws.getRow(satir);
    basliklar.forEach((h, i) => {
      const c = hr.getCell(i + 1);
      c.value = h.header;
      c.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 10 };
      c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: HEAD_FILL } };
      c.alignment = { vertical: "middle", horizontal: h.num ? "right" : "left", wrapText: true };
    });
    hr.height = 20;
  };
  const toplamSatiri = (ws: WS, satir: number, sutun: number) => {
    const r = ws.getRow(satir);
    for (let c = 1; c <= sutun; c++) {
      const cell = r.getCell(c);
      cell.font = { bold: true, color: { argb: INK } };
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: SUBTLE } };
      cell.border = { top: { style: "medium", color: { argb: INK } } };
    }
  };

  // ═══════════════ 1) GENEL DURUM ═══════════════
  const ozet = wb.addWorksheet("Genel Durum");
  ozet.columns = [{ width: 30 }, { width: 14 }, { width: 14 }, { width: 14 }, { width: 14 }, { width: 14 }, { width: 16 }, { width: 18 }];
  const t = ozet.getCell("A1");
  t.value = `ENVANTER RAPORU — ${lotGorunumu ? "LOT BAZINDA" : "ÜRÜN BAZINDA"}`;
  t.font = { size: 16, bold: true, color: { argb: INK } };
  ozet.mergeCells("A1:H1");
  ozet.getRow(1).height = 24;
  const alt = ozet.getCell("A2");
  alt.value = `${data.facilityName} · ${raporZamanMetni(data.generatedAt)} · Hazırlayan: ${data.generatedBy}`;
  alt.font = { size: 10, color: { argb: MUTED } };
  ozet.mergeCells("A2:H2");

  let sat = 4;
  for (const s of suzgecOzeti(data.suzgec)) {
    const r = ozet.getRow(sat++);
    r.getCell(1).value = s.etiket;
    r.getCell(1).font = { size: 9.5, bold: true, color: { argb: MUTED } };
    r.getCell(2).value = s.deger;
    ozet.mergeCells(r.number, 2, r.number, 8);
  }
  const mt = ozet.getRow(sat++);
  mt.getCell(1).value = "Maliyet tabanı";
  mt.getCell(1).font = { size: 9.5, bold: true, color: { argb: MUTED } };
  mt.getCell(2).value = data.maliyetTabani;
  mt.getCell(2).alignment = { wrapText: true, vertical: "top" };
  ozet.mergeCells(mt.number, 2, mt.number, 8);
  mt.height = 30;

  // KPI şeridi
  sat++;
  const kpiler: Array<[string, number, string?]> = [
    ["Ürün", data.ozet.urunSayisi, "#,##0"],
    ["Lot", data.ozet.lotSayisi, "#,##0"],
    [data.ozet.degerBilinmeyenLot > 0 ? "Stok Değeri (en az)" : "Stok Değeri", data.ozet.toplamDeger, MONEY_FMT],
    ["Değeri Bilinmeyen Lot", data.ozet.degerBilinmeyenLot, "#,##0"],
    // "Lot" = artı bakiyeli lot (sütunla aynı); kayıt açığı lotu AYRI etiketle — iki tanım tek etikette basılmaz.
    ...(data.ozet.acikLotSayisi > 0 ? [["Kayıt Açığı Lotu", data.ozet.acikLotSayisi, "#,##0"] as [string, number, string]] : []),
  ];
  kpiler.forEach(([lbl, val, fmt], i) => {
    const l = ozet.getRow(sat).getCell(1 + i * 2);
    l.value = lbl;
    l.font = { size: 9, bold: true, color: { argb: MUTED } };
    const v = ozet.getRow(sat + 1).getCell(1 + i * 2);
    v.value = val;
    v.font = { size: 14, bold: true, color: { argb: INK } };
    v.alignment = { horizontal: "left" };
    if (fmt) v.numFmt = fmt;
  });
  sat += 3;

  // Birim bazında toplam — karışık birimler TOPLANMAZ.
  const bb = ozet.getCell(`A${sat}`);
  bb.value = "BİRİM BAZINDA TOPLAM";
  bb.font = { size: 11, bold: true, color: { argb: INK } };
  sat++;
  const birimBaslik = [{ header: "Birim" }, { header: "Ürün", num: "#,##0" }, ...siniflar.map((s) => ({ header: SINIF_ETIKET[s], num: QTY_FMT })), { header: "Net", num: QTY_FMT }, { header: "Değeri Bilinmeyen", num: QTY_FMT }];
  baslikSatiri(ozet, sat++, birimBaslik);
  for (const b of data.ozet.birimler) {
    const r = ozet.getRow(sat++);
    const vals: unknown[] = [b.uom, b.urun, ...siniflar.map((s) => b[s]), b.net, b.degerBilinmeyen > 0 ? b.degerBilinmeyen : null];
    vals.forEach((v, i) => {
      const c = r.getCell(i + 1);
      c.value = v as never;
      const f = birimBaslik[i]?.num;
      if (f) { c.numFmt = f; c.alignment = { horizontal: "right" }; }
    });
  }
  if (data.ozet.birimler.length === 0) ozet.getRow(sat++).getCell(1).value = "Süzgece uyan stok yok.";
  sat++;

  // Kart türüne göre özet (değer ₺ — para birimi tek, birimden bağımsız toplanabilir).
  const rowsByGroup = new Map<string, StockReportRow[]>();
  for (const r of data.rows) {
    const g = grupAdi(r.groupName);
    rowsByGroup.set(g, [...(rowsByGroup.get(g) ?? []), r]);
  }
  const lotsByGroup = new Map<string, StockReportLot[]>();
  for (const l of data.lotlar) {
    const g = grupAdi(l.groupName);
    lotsByGroup.set(g, [...(lotsByGroup.get(g) ?? []), l]);
  }
  const gruplar = gruplariSirala(rowsByGroup.keys());
  const kt = ozet.getCell(`A${sat}`);
  kt.value = "KART TÜRÜNE GÖRE";
  kt.font = { size: 11, bold: true, color: { argb: INK } };
  sat++;
  const grupBaslik = [{ header: "Kart Türü" }, { header: "Ürün", num: "#,##0" }, { header: "Lot", num: "#,##0" }, { header: "Stok Değeri (₺)", num: MONEY_FMT }, { header: "Değeri Bilinmeyen Ürün", num: "#,##0" }];
  baslikSatiri(ozet, sat++, grupBaslik);
  const grupIlk = sat;
  for (const g of gruplar) {
    const rows = rowsByGroup.get(g)!;
    const r = ozet.getRow(sat++);
    r.getCell(1).value = g;
    r.getCell(2).value = rows.length;
    r.getCell(3).value = rows.reduce((s, x) => s + x.lotCount, 0); // artı bakiyeli lot — sayfa TOPLAM satırıyla aynı
    r.getCell(4).value = Math.round(rows.reduce((s, x) => s + x.stockValue, 0) * 100) / 100;
    r.getCell(4).numFmt = MONEY_FMT;
    r.getCell(5).value = rows.filter((x) => x.degerBilinmeyen > 0).length;
    for (let c = 2; c <= 5; c++) r.getCell(c).alignment = { horizontal: "right" };
  }
  if (gruplar.length) {
    ozet.autoFilter = { from: { row: grupIlk - 1, column: 1 }, to: { row: sat - 1, column: grupBaslik.length } };
    const tr = ozet.getRow(sat);
    tr.getCell(1).value = "TOPLAM";
    tr.getCell(2).value = data.ozet.urunSayisi;
    tr.getCell(3).value = data.ozet.lotSayisi;
    tr.getCell(4).value = data.ozet.toplamDeger;
    tr.getCell(4).numFmt = MONEY_FMT;
    tr.getCell(5).value = data.rows.filter((x) => x.degerBilinmeyen > 0).length;
    for (let c = 2; c <= 5; c++) tr.getCell(c).alignment = { horizontal: "right" };
    toplamSatiri(ozet, sat, grupBaslik.length);
    sat++;
  }
  sat++;

  // Uyarılar — sessizlik yasağı: belgede de SÖYLENİR.
  const notlar: string[] = raporUyarilari(data).map((u) => u.metin); // ekranla AYNI uyarı listesi
  for (const s of siniflar) notlar.push(`${SINIF_ETIKET[s]}: ${SINIF_TANIM[s]}`);
  for (const n of notlar) {
    const c = ozet.getRow(sat).getCell(1);
    c.value = n;
    c.alignment = { wrapText: true, vertical: "top" };
    ozet.mergeCells(sat, 1, sat, 8);
    ozet.getRow(sat).height = Math.min(90, 15 * Math.ceil(n.length / 120));
    sat++;
  }

  // ═══════════════ 2) KART TÜRÜ BAŞINA DETAY SAYFASI ═══════════════
  const kolonlar = lotGorunumu ? lotKolonlari() : urunKolonlari(data);
  const used = new Set<string>(["genel durum"]);
  for (const g of gruplar) {
    const satirlar: unknown[] = lotGorunumu ? lotsByGroup.get(g) ?? [] : rowsByGroup.get(g) ?? [];
    if (lotGorunumu && satirlar.length === 0) continue; // lotu olmayan tür: lot sayfası boş olurdu (Genel Durum notu söyler)
    const ws = wb.addWorksheet(safeSheetName(g, used), { views: [{ state: "frozen", ySplit: 4 }] });
    ws.columns = kolonlar.map((c) => ({ width: c.width }));
    const gt = ws.getCell("A1");
    gt.value = g;
    gt.font = { size: 13, bold: true, color: { argb: INK } };
    ws.mergeCells(1, 1, 1, kolonlar.length);
    const gs = ws.getCell("A2");
    const grupRows = rowsByGroup.get(g)!;
    // "lot" = artı bakiyeli lot (sütun/TOPLAM ile aynı); kayıt açığı lotu ayrı etiketle.
    const grupLot = grupRows.reduce((s, x) => s + x.lotCount, 0);
    const grupAcik = grupRows.reduce((s, x) => s + x.acikLotCount, 0);
    gs.value = `${grupRows.length} ürün · ${grupLot} lot${grupAcik ? ` · ${grupAcik} kayıt açığı lotu` : ""} · ${data.facilityName} · ${lotGorunumu ? "Lot bazında" : "Ürün bazında"}`;
    gs.font = { size: 9.5, color: { argb: MUTED } };
    ws.mergeCells(2, 1, 2, kolonlar.length);
    baslikSatiri(ws, 4, kolonlar);

    satirlar.forEach((x, idx) => {
      const row = ws.getRow(5 + idx);
      kolonlar.forEach((c, i) => {
        const cell = row.getCell(i + 1);
        cell.value = (c.hucre(x as never) ?? null) as never;
        if (c.num) { cell.numFmt = c.num; cell.alignment = { horizontal: "right" }; }
      });
      if (idx % 2 === 1) for (let c = 1; c <= kolonlar.length; c++) row.getCell(c).fill = { type: "pattern", pattern: "solid", fgColor: { argb: ZEBRA } };
      // Sınıf hücresi (lot görünümü) / sınıf sütunları (ürün görünümü): DOLGU + METİN.
      if (lotGorunumu) {
        const l = x as StockReportLot;
        const si = kolonlar.findIndex((c) => c.header === "Sınıf") + 1;
        const cell = row.getCell(si);
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: SINIF_FILL[l.sinif] } };
        cell.font = { bold: l.sinif !== "saglam", color: { argb: SINIF_FONT[l.sinif] }, size: 10 };
      } else {
        kolonlar.forEach((c, i) => {
          if (!c.sinif || c.sinif === "saglam") return;
          const v = (x as StockReportRow)[c.sinif];
          if (v !== 0) row.getCell(i + 1).font = { bold: true, color: { argb: SINIF_FONT[c.sinif] } };
        });
      }
    });

    // Sayfa sonu: BİRİM BAZINDA toplam (karışık birim toplanmaz) + değer.
    let ts = 5 + satirlar.length;
    const birimler = [...new Set(grupRows.map((r) => r.uom))].sort((a, b) => a.localeCompare(b, "tr"));
    for (const u of birimler) {
      const rs = grupRows.filter((r) => r.uom === u);
      const tRow = ws.getRow(ts);
      tRow.getCell(1).value = `TOPLAM (${u})`;
      const put = (header: string, val: number, fmt: string) => {
        const i = kolonlar.findIndex((c) => c.header === header) + 1;
        if (i <= 0) return;
        const c = tRow.getCell(i);
        c.value = val; c.numFmt = fmt; c.alignment = { horizontal: "right" };
      };
      const sum = (f: (r: StockReportRow) => number) => Math.round(rs.reduce((s, r) => s + f(r), 0) * 1e4) / 1e4;
      if (lotGorunumu) {
        put("Miktar", sum((r) => r.onHand), QTY_FMT);
        put("Değer (₺)", Math.round(rs.reduce((s, r) => s + r.stockValue, 0) * 100) / 100, MONEY_FMT);
      } else {
        for (const s of siniflar) put(SINIF_ETIKET[s], sum((r) => r[s]), QTY_FMT);
        put("Net", sum((r) => r.onHand), QTY_FMT);
        put("Lot", rs.reduce((s, r) => s + r.lotCount, 0), "#,##0");
        put("Stok Değeri (₺)", Math.round(rs.reduce((s, r) => s + r.stockValue, 0) * 100) / 100, MONEY_FMT);
        put("Değeri Bilinmeyen", sum((r) => r.degerBilinmeyen), QTY_FMT);
      }
      toplamSatiri(ws, ts, kolonlar.length);
      ts++;
    }
    ws.autoFilter = { from: { row: 4, column: 1 }, to: { row: 4 + satirlar.length, column: kolonlar.length } };
  }

  return wb;
}

/**
 * Raporu üretir ve tarayıcıda indirir.
 * @returns indirilen dosyanın adı (görünüm + zaman damgalı, benzersiz)
 */
export async function downloadStockReport(data: StockReportData): Promise<string> {
  const wb = await buildStockReportWorkbook(data);
  const buf = await wb.xlsx.writeBuffer();
  const fileName = stockReportFileName(new Date(data.generatedAt), data.suzgec.gorunum);
  const blob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
  return fileName;
}
